import React, { useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, Crop, makeAspectCrop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { typing } from './keys';
import styles from './Kyc.module.css';

/** An ID card's shape — the frame starts like this; the customer can drag it to any shape. */
const CARD = 1.586;
const MAX_SIDE = 2000;

function startFrame(width: number, height: number): Crop {
  return centerCrop(makeAspectCrop({ unit: '%', width: 90 }, CARD, width, height), width, height);
}

/** The photo turned a quarter: drawn on a canvas and handed back as a new picture. */
async function rotated(src: string): Promise<string> {
  const img = await load(src);
  const c = document.createElement('canvas');
  c.width = img.naturalHeight;
  c.height = img.naturalWidth;
  const ctx = c.getContext('2d')!;
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  const blob = await new Promise<Blob>((ok, no) => c.toBlob((b) => (b ? ok(b) : no(new Error('rotate'))), 'image/jpeg', 0.92));
  return URL.createObjectURL(blob);
}

/** iPhone photos (HEIC / HEIF) only open in Safari — turn them into JPEG first, everywhere. */
const isHeic = (f: File) => /image\/hei[cf]/i.test(f.type) || /\.hei[cf]$/i.test(f.name);

async function readable(file: File): Promise<string> {
  if (!isHeic(file)) return URL.createObjectURL(file);
  // Loaded only when needed: the converter is large and most photos don't need it.
  const { default: heic2any } = await import('heic2any');
  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  return URL.createObjectURL(Array.isArray(out) ? out[0] : out);
}

const UNREADABLE = 'This photo could not be opened. Please take a new photo or choose another one.';

const load = (src: string) =>
  new Promise<HTMLImageElement>((ok, no) => {
    const i = new Image();
    i.onload = () => ok(i);
    i.onerror = () => no(new Error(UNREADABLE));
    i.src = src;
  });

/**
 * "Fit the card in the frame": the picked photo with a frame the customer
 * drags to the card's edges, a turn button, and Use this photo. The cut-out
 * comes back as a JPEG of at most 2000px.
 */
const CropDialog: React.FC<{
  file: File;
  title: string;
  onCancel: () => void;
  onDone: (blob: Blob) => void;
}> = ({ file, title, onCancel, onDone }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [pixels, setPixels] = useState<PixelCrop>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const img = useRef<HTMLImageElement>(null);
  const made = useRef<string[]>([]);

  useEffect(() => {
    if (src) made.current.push(src);
  }, [src]);
  // JPG, PNG, WebP, GIF, AVIF open as they are; HEIC / HEIF are converted first.
  useEffect(() => {
    let live = true;
    readable(file)
      .then((url) => (live ? setSrc(url) : URL.revokeObjectURL(url)))
      .catch(() => live && setProblem(UNREADABLE));
    return () => {
      live = false;
    };
  }, [file]);
  // Keys: Esc cancels, R rotates, Enter uses the photo (the latest handlers, without re-binding).
  const keys = useRef({ turn: () => {}, use: () => {} });
  useEffect(() => {
    const urls = made.current;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onCancel();
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
      if (e.key.toLowerCase() === 'r') keys.current.turn();
      else if (e.key === 'Enter') {
        e.preventDefault();
        keys.current.use();
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [onCancel]);

  async function turn() {
    setBusy(true);
    try {
      if (!src) return;
      setCrop(undefined);
      setSrc(await rotated(src));
    } catch {
      setProblem('Could not turn the photo');
    } finally {
      setBusy(false);
    }
  }

  async function use() {
    const el = img.current;
    if (!el || !pixels || !pixels.width || !pixels.height) return;
    setBusy(true);
    try {
      // The frame is measured on the photo as shown; scale it to the real pixels.
      const sx = el.naturalWidth / el.width;
      const sy = el.naturalHeight / el.height;
      const w = pixels.width * sx;
      const h = pixels.height * sy;
      const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
      const c = document.createElement('canvas');
      c.width = Math.round(w * scale);
      c.height = Math.round(h * scale);
      c.getContext('2d')!.drawImage(el, pixels.x * sx, pixels.y * sy, w, h, 0, 0, c.width, c.height);
      const blob = await new Promise<Blob>((ok, no) => c.toBlob((b) => (b ? ok(b) : no(new Error('crop'))), 'image/jpeg', 0.88));
      onDone(blob);
    } catch {
      setProblem('Could not use this photo — please try another');
      setBusy(false);
    }
  }

  keys.current = {
    turn: () => !busy && !problem && src && turn(),
    use: () => !busy && !problem && pixels && use(),
  };

  return (
    <div className={styles.cropScrim} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.cropBox}>
        <div className={styles.cropHead}>
          <div>
            <h3>{title}</h3>
            <p>Drag the corners to the edges of the card</p>
          </div>
          <button type="button" className={styles.cropX} onClick={onCancel} aria-label="Close">
            ✕
          </button>
        </div>
        <div className={styles.cropStage}>
          {problem ? (
            <p className={styles.cropProblem}>{problem}</p>
          ) : !src ? (
            <p className={styles.cropProblem}>Opening the photo…</p>
          ) : (
            <ReactCrop crop={crop} onChange={(_, pct) => setCrop(pct)} onComplete={setPixels} keepSelection ruleOfThirds minWidth={40} minHeight={30}>
              <img
                ref={img}
                src={src}
                alt=""
                className={styles.cropImg}
                onLoad={(e) => {
                  const { width, height } = e.currentTarget;
                  const start = startFrame(width, height);
                  setCrop(start);
                  setPixels({
                    unit: 'px',
                    x: ((start.x ?? 0) / 100) * width,
                    y: ((start.y ?? 0) / 100) * height,
                    width: ((start.width ?? 0) / 100) * width,
                    height: ((start.height ?? 0) / 100) * height,
                  });
                }}
                onError={() => setProblem(UNREADABLE)}
              />
            </ReactCrop>
          )}
        </div>
        <div className={styles.cropFoot}>
          <button type="button" className={`${styles.btn} ${styles.gh}`} onClick={turn} disabled={busy || !!problem || !src} title="Photo is sideways? Rotate it a quarter turn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden style={{ marginRight: 6, verticalAlign: '-3px' }}>
              <path d="M21 12a9 9 0 1 1-3-6.7" />
              <path d="M21 3v6h-6" />
            </svg>
            Rotate <kbd className={styles.kbd}>R</kbd>
          </button>
          <span className={styles.cropGap} />
          <button type="button" className={`${styles.btn} ${styles.gh}`} onClick={onCancel} disabled={busy}>
            Cancel <kbd className={styles.kbd}>Esc</kbd>
          </button>
          <button type="button" className={styles.btn} onClick={use} disabled={busy || !!problem || !pixels}>
            {busy ? 'Please wait…' : 'Use this photo'} <kbd className={styles.kbd}>Enter</kbd>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CropDialog;
